import type { DraftValidationDetail } from "../../api/queries/adminQueries";
import { ComposerEditPanel } from "./ComposerEditPanel";
import { DraftGenerationSummaryPanel } from "./DraftGenerationSummary";
import { DraftReviewSummary } from "./DraftReviewSummary";
import { DraftEditorSection, type DraftBodyEditorRef } from "./DraftEditorSection";
import { ThreadContextPanel } from "./ThreadContextPanel";

type DraftDetailPanelProps = {
  selectedDraftUid: string | null;
  draftDetail: DraftValidationDetail | undefined;
  isLoading: boolean;
  editSubject: string;
  editTo: string[];
  onSubjectChange: (v: string) => void;
  onToChange: (v: string[]) => void;
  onSave: () => void;
  isSaving: boolean;
  bodyEditorRef: React.RefObject<DraftBodyEditorRef | null>;
  formatDate: (iso: string) => string;
};

export const DraftDetailPanel = ({
  selectedDraftUid,
  draftDetail,
  isLoading,
  editSubject,
  editTo,
  onSubjectChange,
  onToChange,
  onSave,
  isSaving,
  bodyEditorRef,
  formatDate,
}: DraftDetailPanelProps) => (
  <div className="w-full rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
    <h2 className="mb-4 text-lg font-semibold text-slate-900">Draft Details</h2>
    {!selectedDraftUid ? (
      <p className="text-sm text-slate-600">Select a draft from the list to view details.</p>
    ) : isLoading ? (
      <p className="text-sm text-slate-600">Loading details…</p>
    ) : draftDetail ? (
      <div className="space-y-6">
        <div>
          <h3 className="mb-2 text-sm font-semibold text-slate-700">
            Customer & Payments
          </h3>
          <div className="space-y-1 text-sm text-slate-600">
            <p>
              <span className="font-medium">Customer:</span> {draftDetail.customer_name}
            </p>
            <p>
              <span className="font-medium">Payments:</span>{" "}
              {draftDetail.payment_external_ids?.join(", ") || "–"}
            </p>
          </div>
        </div>

        {draftDetail.generation ? (
          <DraftGenerationSummaryPanel
            generation={draftDetail.generation}
            formatDate={formatDate}
          />
        ) : draftDetail.trace_id ? (
          <p className="text-sm text-slate-500">
            Trace{" "}
            <code className="rounded bg-slate-100 px-1 py-0.5 text-xs">
              {draftDetail.trace_id}
            </code>{" "}
            exists but generation metadata is not loaded.
          </p>
        ) : (
          <p className="text-sm text-slate-500">
            No generation trace for this draft. Template-based drafts are not traced; LLM drafts
            are traced on the next refresh after deploy when draft eval is enabled.
          </p>
        )}

        {draftDetail.composer_edit ? (
          <ComposerEditPanel composerEdit={draftDetail.composer_edit} />
        ) : draftDetail.trace_id || draftDetail.generation ? (
          <p className="text-sm text-slate-500">No composer edits recorded yet.</p>
        ) : null}

        <DraftReviewSummary detail={draftDetail} />

        <DraftEditorSection
          ref={bodyEditorRef}
          draftUid={selectedDraftUid}
          initialHtml={draftDetail.draft_text?.trim() || ""}
          subject={editSubject}
          to={editTo}
          onSubjectChange={onSubjectChange}
          onToChange={onToChange}
          onSave={onSave}
          isSaving={isSaving}
        />

        <ThreadContextPanel detail={draftDetail} formatDate={formatDate} />
      </div>
    ) : (
      <p className="text-sm text-red-600">Failed to load draft details.</p>
    )}
  </div>
);
